/**
 * PremiumPackagesActionsService
 *
 * Domain service that owns the live-sync of the current user's premium
 * packages from Firestore into SecureStorage. Mirrors Flutter's
 * `PremiumPackagesService.listenToPremiumPackagesChanges`
 * (lib/services/premium_packages_service.dart:13-70).
 *
 * Architecture:
 * UseCase → PremiumPackagesActionsService → Data layer (firestoreService)
 */

import type { Unsubscribe } from 'firebase/firestore';

import { firestoreService } from '@/data/services/firestoreService';
import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { UserSettingStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { InAppPurchaseStatus } from '@/domain/enums/premiumPackages/InAppPurchaseStatus';
import { UserPremiumPackageRemapper } from '@/domain/remappers/userPremiumPackage/userPremiumPackageRemapper';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class PremiumPackagesActionsService {
  private static instance: PremiumPackagesActionsService;

  private constructor() {}

  static getInstance(): PremiumPackagesActionsService {
    if (!this.instance) {
      this.instance = new PremiumPackagesActionsService();
    }
    return this.instance;
  }

  /**
   * Listen to live Firestore changes of the current user's premium packages
   * and mirror each change into SecureStorage.
   *
   * Mirrors Flutter's PremiumPackagesService.listenToPremiumPackagesChanges
   * (lib/services/premium_packages_service.dart:13-70):
   *   - added/modified  → write the remapped storage entry; if status is
   *                       `expired`, reset all settings gated by the package
   *                       to their `defaultValue`.
   *   - removed         → delete the local entry and reset matching settings.
   *
   * Per-change errors are logged via appLogger.error and swallowed so a single
   * bad doc cannot break the listener. Returns the underlying Firestore
   * Unsubscribe; the caller is responsible for cleanup.
   */
  listenToPremiumPackagesChanges(userId: string): Unsubscribe {
    const secureStorage = SecureStorageWebCrypto.getInstance();

    return firestoreService.subscribeToUserPremiumPackagesLiveChanges(
      userId,
      async changes => {
        for (const change of changes) {
          if (!change.data || typeof change.data !== 'object' || !('productId' in change.data)) {
            appLogger.error(
              'Premium package change has no data or missing productId',
              'PremiumPackagesActionsService', { context: { type: change.type } },
            );
            continue;
          }
          const dto = UserPremiumPackageRemapper.fromJson(change.data);
          const key = SECURE_STORAGE_KEYS.USER_PREMIUM_PACKAGES + dto.productId;

          try {
            if (change.type === 'removed') {
              await secureStorage.remove(key);
              await this._resetPremiumSettings(dto.productId);
            } else {
              const storage = UserPremiumPackageRemapper.toStorage(dto);
              await secureStorage.setEncrypted(key, storage);
              if (dto.status === InAppPurchaseStatus.expired) {
                await this._resetPremiumSettings(dto.productId);
              }
            }
          } catch (error) {
            appLogger.error(
              'Error syncing user premium package change',
              'PremiumPackagesActionsService', { context: { type: change.type, productId: dto.productId }, error: error as Error },
            );
          }
        }
      },
      error => {
        appLogger.error(
          'Error in premium packages snapshot listener',
          'PremiumPackagesActionsService', { error: error as Error },
        );
      },
    );
  }

  /**
   * Reset every user setting gated by `productId` back to its `defaultValue`.
   *
   * Mirrors Flutter's PremiumPackagesService._resetPremiumSettings
   * (lib/services/premium_packages_service.dart:61-70).
   */
  private async _resetPremiumSettings(productId: string): Promise<void> {
    const secureStorage = SecureStorageWebCrypto.getInstance();
    const settings = await secureStorage.getAllWithPrefix<UserSettingStorage>(
      SECURE_STORAGE_KEYS.USER_SETTINGS,
    );
    const matching = settings.filter(setting => setting.premiumPackage === productId);
    for (const setting of matching) {
      await secureStorage.setEncrypted(
        SECURE_STORAGE_KEYS.USER_SETTINGS + setting.name,
        { ...setting, value: setting.defaultValue },
      );
    }
  }
}

// Export singleton instance for convenience
export const premiumPackagesActionsService = PremiumPackagesActionsService.getInstance();
